import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Table, Input, Space, Typography, Card, Statistic, Row, Col, Tag, Empty, Spin, Button } from 'antd';
import { SearchOutlined, SwapOutlined, PlusOutlined, MinusOutlined, EditOutlined, DeleteOutlined, FilterFilled, ArrowUpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_self_movements, get_self_movements_summary } from '../../../redux/actions/selfActions';

// Utils
import { get_movement_type_desc } from '../../../utils/get_status_desc';
import { get_movement_type_color } from '../../../utils/get_color_status';

const { Title, Text } = Typography;

const LoadFileMovements = ({ historyId }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const searchInput = useRef(null);

	// Redux state
	const { self_movements_loading, self_movements, self_movements_summary_loading, self_movements_summary } = useSelector(state => state.self);

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [locationFilter, setLocationFilter] = useState('');
	const [movementTypeFilter, setMovementTypeFilter] = useState('');

	// Fetch movements on component mount and when filters change
	useEffect(() => {
		if (historyId) {
			fetchMovements();
		}
	}, [historyId, currentPage, pageSize, locationFilter, movementTypeFilter]);

	const fetchMovements = () => {
		const filters = {
			load_history: historyId,
			skip: (currentPage - 1) * pageSize,
			limit: pageSize
		};

		// Add server-side filters
		if (locationFilter) {
			filters.location_code = locationFilter;
		}

		if (movementTypeFilter) {
			filters.movement_type = movementTypeFilter;
		}

		dispatch(get_self_movements(filters));
		dispatch(get_self_movements_summary(historyId));
	};

	// Handle pagination and filter changes
	const handleTableChange = (pagination, filters, sorter) => {
		// Update pagination state
		if (pagination.current !== currentPage) {
			setCurrentPage(pagination.current);
		}
		if (pagination.pageSize !== pageSize) {
			setPageSize(pagination.pageSize);
		}

		// Handle movement type filter - this will trigger useEffect to fetch new data
		if (filters.movement_type) {
			const newFilter = filters.movement_type.length > 0 ? filters.movement_type[0] : '';
			if (newFilter !== movementTypeFilter) {
				setMovementTypeFilter(newFilter);
				setCurrentPage(1); // Reset to first page when filter changes
			}
		} else if (movementTypeFilter !== '') {
			setMovementTypeFilter('');
			setCurrentPage(1); // Reset to first page when filter is cleared
		}
	};

	// Search functionality (server-side filtering)
	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();

		// Update the appropriate filter for server request
		if (dataIndex === 'location_code') {
			setLocationFilter(selectedKeys[0]);
		}

		// Reset to first page when applying filters
		setCurrentPage(1);
	};

	const handleReset = (clearFilters, dataIndex) => {
		clearFilters();

		// Clear the appropriate filter
		if (dataIndex === 'location_code') {
			setLocationFilter('');
		}

		// Reset to first page
		setCurrentPage(1);
	};

	// Get column search props
	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
				<Input
					ref={searchInput}
					placeholder={t('loadStatusDetails.movements.searchByCode')}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
					style={{ marginBottom: 8, display: 'block' }}
				/>
				<Space>
					<Button
						type="primary"
						onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
						icon={<SearchOutlined />}
						size="small"
						style={{ width: 90 }}
					>
						{t('buttons.search')}
					</Button>
					<Button
						onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
						size="small"
						style={{ width: 90 }}
					>
						{t('buttons.reset')}
					</Button>
					<Button
						type="link"
						size="small"
						onClick={() => {
							close();
						}}
					>
						{t('buttons.close')}
					</Button>
				</Space>
			</div>
		),
		filterIcon: (filtered) => (
			<SearchOutlined style={{ color: filtered ? 'white' : '#818D9C' }} />
		),
		filteredValue: (dataIndex === 'location_code' && locationFilter) ? [locationFilter] : null,
		onFilterDropdownOpenChange: (visible) => {
			if (visible) {
				setTimeout(() => searchInput.current?.select(), 100);
			}
		}
	});

	// Get movement type icon
	const getMovementTypeIcon = (type) => {
		switch(type) {
			case 1: return <PlusOutlined />;
			case 2: return <EditOutlined />;
			case 3: return <ArrowUpOutlined />;
			case 4: return <SwapOutlined />;
			case 5: return <DeleteOutlined />;
			default: return null;
		}
	};

	// Table columns
	const columns = [
		{
			title: t('routesTable.headers.code') || 'Location Code',
			dataIndex: 'location_code',
			key: 'location_code',
			width: '20%',
			...getColumnSearchProps('location_code')
		},
		{
			title: t('loadStatusDetails.movements.movementType') || 'Movement Type',
			dataIndex: 'movement_type',
			key: 'movement_type',
			width: '15%',
			filters: Array.from({ length: 5 }, (_, i) => i + 1).map(type => ({
				text: get_movement_type_desc(type),
				value: type
			})),
			filterSearch: true,
			filterMultiple: false, // Only allow single selection
			filteredValue: movementTypeFilter ? [movementTypeFilter] : null,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			onFilter: () => true, // Always return true since we're doing server-side filtering
			render: (type) => (
				<Tag
					icon={getMovementTypeIcon(type)}
					color={get_movement_type_color(type)}
				>
					{get_movement_type_desc(type)}
				</Tag>
			)
		},
		{
			title: t('loadStatusDetails.movements.fromRoute') || 'From Route',
			dataIndex: ['route_changes', 'from_route'],
			key: 'from_route',
			width: '20%',
			render: (text) => text || '-'
		},
		{
			title: t('loadStatusDetails.movements.toRoute') || 'To Route',
			dataIndex: ['route_changes', 'to_route'],
			key: 'to_route',
			width: '20%',
			render: (text) => text || '-'
		}
	];

	if (!historyId) {
		return (
			<Empty
				description={t('movements.noFileSelected') || 'No file selected'}
			/>
		);
	}

	return (
		<div style={{ paddingTop: '3rem' }}>
			<Title level={4} style={{ textAlign: 'center', marginBottom: '1rem' }}>
				{t('loadStatusDetails.movements.title') || 'Movement Records'}
			</Title>

			{/* Statistics Cards */}
			<Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
				<Col xs={24} sm={12} md={8} lg={4}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.movements.totalMovements')}
							loading={self_movements_summary_loading}
							value={self_movements_summary?.totals?.total_movements || 0}
							valueStyle={{ color: '#415577' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.routesSummary.addedLoacations')}
							loading={self_movements_summary_loading}
							value={self_movements_summary?.totals?.new_locations || 0}
							prefix={<PlusOutlined />}
							valueStyle={{ color: '#52c41a' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.movements.updateLocations')}
							loading={self_movements_summary_loading}
							value={self_movements_summary?.totals?.updated_locations || 0}
							prefix={<EditOutlined />}
							valueStyle={{ color: '#1890ff' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.movements.changedLocations')}
							loading={self_movements_summary_loading}
							value={self_movements_summary?.totals?.changed_routes || 0}
							prefix={<SwapOutlined />}
							valueStyle={{ color: '#fa8c16' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.routesSummary.removedLocations')}
							loading={self_movements_summary_loading}
							value={self_movements_summary?.totals?.removed_locations || 0}
							prefix={<MinusOutlined />}
							valueStyle={{ color: '#ff4d4f' }}
						/>
					</Card>
				</Col>
			</Row>

			{/* Movements Table */}
			<Spin spinning={self_movements_loading} tip={t('loadStatus.statusTable.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={self_movements?.data || []}
					rowKey={(record) => `${record.location_code}-${record.movement_type}-${record._id?.$oid || Math.random()}`}
					pagination={{
						current: currentPage,
						pageSize: pageSize,
						total: self_movements?.count || 0,
						showSizeChanger: true,
						pageSizeOptions: ['10', '25', '50', '100'],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} ${t('pagination.of')} ${total}`,
						showQuickJumper: self_movements?.count > 100,
						locale: {
							items_per_page: t('pagination.rows')
						}
					}}
					onChange={handleTableChange}
					scroll={{ x: 800, y: 600 }}
					size="middle"
					locale={{
						emptyText: !self_movements_loading && (
							<Empty
								description={
									<Text type='secondary'>{t('loadStatusDetails.movements.noMovements') || 'No movements found'}</Text>
								}
							/>
						),
						filterReset: t('buttons.reset'),
						filterSearchPlaceholder: t('buttons.search')
					}}
				/>
			</Spin>
		</div>
	);
};

LoadFileMovements.propTypes = {
	history: PropTypes.string
};

export default LoadFileMovements;